import { BorderProps, ColorProps } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { Flex, FlexProps, Paragraph } from '../../atoms';

type InfoBoxVariant = 'info' | 'warning' | 'error';

type VariantObjectReturnType = {
  backgroundColor: ColorProps['color'];
  border?: BorderProps;
};

const variantContent: {
  [key in InfoBoxVariant]: VariantObjectReturnType;
} = {
  info: {
    backgroundColor: 'background.surface'
  },
  warning: {
    backgroundColor: 'background.status.default.warning',
    border: {
      border: '1px solid',
      borderColor: 'background.container.warning'
    }
  },
  error: {
    backgroundColor: 'background.status.default.alert',
    border: {
      border: '1px solid',
      borderColor: 'background.container.alert'
    }
  }
};

type Props = {
  title?: string;
  text?: string;
  variant?: InfoBoxVariant;
  children?: ReactNode;
} & FlexProps;

function InfoBoxFooter({ children }: { children?: ReactNode }): ReactNode {
  return (
    <Flex flexDirection="column" gap="space_unit.x1" alignItems="flex-start">
      {children}
    </Flex>
  );
}

export function InfoBox({
  title,
  text,
  variant = 'info',
  children,
  ...rest
}: Props): ReactNode {
  const content = variantContent[variant];

  return (
    <Flex
      flexDirection="column"
      padding="space_unit.x3"
      backgroundColor={content.backgroundColor}
      border={content.border?.border}
      borderColor={content.border?.borderColor}
      borderRadius="radii.x4"
      gap={title && 'space_unit.x2'}
      {...rest}
    >
      <Flex flexDirection="column">
        {title && (
          <Paragraph size="md" fontWeight="500">
            {title}
          </Paragraph>
        )}
        {text && <Paragraph size="md">{text}</Paragraph>}
      </Flex>
      {children}
    </Flex>
  );
}

InfoBox.Footer = InfoBoxFooter;
